/**
 * Insurance Policy Detail Page — F007
 *
 * Sections:
 * - PI Alert banner (if PI and alertLevel set)
 * - Policy Information (read-only, Edit button)
 * - Coverage Section (coverAmount, excessAmount, premiumAmount, coverageSummary)
 * - Endorsements Section (table + Add Endorsement button)
 * - AFG Submission Section (PI only — shown as "coming soon")
 *
 * Schema divergence notes:
 *  - NO brokerContact, brokerEmail, brokerPhone (spec has them; schema does NOT)
 *  - NO exclusionsSummary (schema does NOT have this)
 *  - NO AFG fields (afgSubmissionDate, afgReference don't exist in schema)
 *  - coverAmount NOT coverageAmount
 */

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRoute, useLocation } from "wouter";
import { format } from "date-fns";
import {
  AlertTriangle,
  ArrowLeft,
  Edit,
  FileText,
  Loader2,
  Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { apiRequest } from "@/lib/queryClient";
import { ComplianceLayout } from "./compliance-layout";
import { InsurancePolicyModal } from "@/components/modals/insurance-policy-modal";
import { InsuranceEndorsementModal } from "@/components/modals/insurance-endorsement-modal";
import { AfgSubmissionModal } from "@/components/modals/afg-submission-modal";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface InsuranceEndorsement {
  id: string;
  insurancePolicyId: string;
  endorsementType: string;
  endorsementNumber: string | null;
  description: string;
  effectiveDate: string;
  premiumAdjustment: string | null;
  documentUrl: string | null;
  createdAt: string;
}

interface InsurancePolicyDetail {
  id: string;
  insuranceType: string;
  policyNumber: string | null;
  policyName: string;
  insurerName: string;
  brokerName: string | null;
  coverAmount: string | null;
  excessAmount: string | null;
  coverageSummary: string | null;
  inceptionDate: string | null;
  expiryDate: string | null;
  renewalReminderDate: string | null;
  policyStatus: string;
  premiumAmount: string | null;
  premiumFrequency: string | null;
  policyDocumentUrl: string | null;
  certificateUrl: string | null;
  createdAt: string;
  updatedAt: string;
  // Calculated fields
  daysUntilExpiry: number | null;
  alertLevel: string | null;
  // Joined
  endorsements: InsuranceEndorsement[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ALERT_BANNER_STYLES: Record<string, string> = {
  info: "border-blue-300 bg-blue-50 text-blue-800",
  warning: "border-yellow-300 bg-yellow-50 text-yellow-800",
  urgent: "border-orange-300 bg-orange-50 text-orange-800",
  critical: "border-red-300 bg-red-100 text-red-800",
  blocking: "border-red-500 bg-red-200 text-red-900",
};

const ALERT_BANNER_MESSAGES: Record<string, (days: number | null) => string> = {
  info: (days) => `PI Insurance expires in ${days} days`,
  warning: (days) => `PI Insurance expires in ${days} days — action required`,
  urgent: (days) => `PI Insurance expires in ${days} days — urgent action needed`,
  critical: (days) => `PI Insurance expires in ${days} days — broker applications may be blocked`,
  blocking: () => "PI Insurance has expired — application lodgement blocked until renewed",
};

const POLICY_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  active: { label: "Active", className: "bg-green-100 text-green-700 border-green-300" },
  expired: { label: "Expired", className: "bg-red-100 text-red-700 border-red-300" },
  cancelled: { label: "Cancelled", className: "bg-gray-100 text-gray-700 border-gray-300" },
  pending_renewal: { label: "Pending Renewal", className: "bg-amber-100 text-amber-700 border-amber-300" },
};

const INSURANCE_TYPE_LABELS: Record<string, string> = {
  pi: "Professional Indemnity",
  public_liability: "Public Liability",
  cyber: "Cyber",
  management_liability: "Management Liability",
  other: "Other",
};

const ENDORSEMENT_TYPE_LABELS: Record<string, string> = {
  addition: "Addition",
  deletion: "Deletion",
  amendment: "Amendment",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "-";
  try {
    return format(new Date(value), "dd/MM/yyyy");
  } catch {
    return value;
  }
};

const formatCurrency = (value: string | null | undefined): string => {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
  }).format(num);
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface InfoRowProps {
  label: string;
  value: string | null | undefined;
  testId?: string;
}

function InfoRow({ label, value, testId }: InfoRowProps) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium" data-testid={testId}>
        {value ?? "-"}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main detail component
// ---------------------------------------------------------------------------

function InsuranceDetailContent() {
  const [, params] = useRoute("/compliance/insurance/:id");
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();

  const policyId = params?.id ?? "";

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEndorsementModalOpen, setIsEndorsementModalOpen] = useState(false);
  const [isAfgModalOpen, setIsAfgModalOpen] = useState(false);

  const { data: policy, isLoading } = useQuery<InsurancePolicyDetail>({
    queryKey: [`/api/compliance/insurance/${policyId}`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/compliance/insurance/${policyId}`);
      if (!response.ok) throw new Error("Policy not found");
      return response.json();
    },
    enabled: !!policyId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!policy) {
    return (
      <div className="mx-auto max-w-4xl py-12 text-center text-muted-foreground">
        <p>Insurance policy not found.</p>
        <Button
          variant="ghost"
          onClick={() => setLocation("/compliance/insurance")}
          className="mt-2"
        >
          Back to Insurance Policies
        </Button>
      </div>
    );
  }

  const isPi = policy.insuranceType === "pi";

  // Render alert banner
  const renderAlertBanner = () => {
    if (!policy.alertLevel) return null;
    const bannerClass = ALERT_BANNER_STYLES[policy.alertLevel] ?? "";
    const getMessage = ALERT_BANNER_MESSAGES[policy.alertLevel];
    const message = getMessage ? getMessage(policy.daysUntilExpiry) : "";

    return (
      <div
        className={cn("flex items-center gap-2 rounded-lg border p-4", bannerClass)}
        data-testid="pi-alert-banner"
        data-alert-level={policy.alertLevel}
      >
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span className="text-sm font-medium">{message}</span>
      </div>
    );
  };

  const statusConfig = POLICY_STATUS_CONFIG[policy.policyStatus] ?? {
    label: policy.policyStatus,
    className: "bg-gray-100 text-gray-700 border-gray-300",
  };

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      {/* Back navigation */}
      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setLocation("/compliance/insurance")}
          data-testid="button-back"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Insurance Policies
        </Button>
      </div>

      {/* Alert banner */}
      {renderAlertBanner()}

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold" data-testid="policy-name">
            {policy.policyName}
          </h1>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {INSURANCE_TYPE_LABELS[policy.insuranceType] ?? policy.insuranceType}
            </span>
            <Badge
              variant="outline"
              className={statusConfig.className}
              data-testid="policy-status-badge"
            >
              {statusConfig.label}
            </Badge>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsEditModalOpen(true)}
          data-testid="button-edit-policy"
        >
          <Edit className="h-4 w-4 mr-1" />
          Edit Policy
        </Button>
      </div>

      {/* Policy Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Policy Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <InfoRow
              label="Policy Number"
              value={policy.policyNumber}
              testId="policy-number"
            />
            <InfoRow
              label="Insurer"
              value={policy.insurerName}
              testId="insurer-name"
            />
            <InfoRow
              label="Broker"
              value={policy.brokerName}
              testId="broker-name"
            />
            <InfoRow
              label="Inception Date"
              value={formatDate(policy.inceptionDate)}
              testId="inception-date"
            />
            <InfoRow
              label="Expiry Date"
              value={formatDate(policy.expiryDate)}
              testId="expiry-date"
            />
            <InfoRow
              label="Renewal Reminder"
              value={formatDate(policy.renewalReminderDate)}
              testId="renewal-reminder-date"
            />
          </div>

          {/* Document links */}
          {(policy.policyDocumentUrl || policy.certificateUrl) && (
            <div className="mt-4 flex gap-3">
              {policy.policyDocumentUrl && (
                <a
                  href={policy.policyDocumentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline"
                  data-testid="link-policy-document"
                >
                  <FileText className="h-3.5 w-3.5" />
                  Policy Document
                </a>
              )}
              {policy.certificateUrl && (
                <a
                  href={policy.certificateUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline"
                  data-testid="link-certificate"
                >
                  <FileText className="h-3.5 w-3.5" />
                  Certificate
                </a>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Coverage */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Coverage</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <InfoRow
              label="Cover Amount"
              value={formatCurrency(policy.coverAmount)}
              testId="cover-amount"
            />
            <InfoRow
              label="Excess Amount"
              value={formatCurrency(policy.excessAmount)}
              testId="excess-amount"
            />
            <InfoRow
              label="Premium Amount"
              value={formatCurrency(policy.premiumAmount)}
              testId="premium-amount"
            />
            {policy.premiumFrequency && (
              <InfoRow
                label="Premium Frequency"
                value={policy.premiumFrequency}
                testId="premium-frequency"
              />
            )}
          </div>
          {policy.coverageSummary && (
            <div className="mt-4">
              <span className="text-xs text-muted-foreground">Coverage Summary</span>
              <p className="mt-1 text-sm" data-testid="coverage-summary">
                {policy.coverageSummary}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* AFG Submission Section — PI only, coming soon */}
      {isPi && (
        <Card data-testid="afg-section">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">AFG Submission</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsAfgModalOpen(true)}
                data-testid="button-afg-submit"
              >
                Record AFG Submission
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              AFG submission tracking is coming soon. Schema fields for
              afgSubmissionDate and afgReference are not yet available.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Endorsements */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Endorsements</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEndorsementModalOpen(true)}
              data-testid="button-add-endorsement"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Endorsement
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Number</TableHead>
                <TableHead>Effective Date</TableHead>
                <TableHead>Description</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {policy.endorsements.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="py-8 text-center text-sm text-muted-foreground"
                  >
                    No endorsements added yet
                  </TableCell>
                </TableRow>
              ) : (
                policy.endorsements.map((endorsement) => (
                  <TableRow
                    key={endorsement.id}
                    data-testid={`row-endorsement-${endorsement.id}`}
                  >
                    <TableCell>
                      {ENDORSEMENT_TYPE_LABELS[endorsement.endorsementType] ??
                        endorsement.endorsementType}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {endorsement.endorsementNumber ?? "-"}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatDate(endorsement.effectiveDate)}
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-sm">
                      {endorsement.description}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Policy Modal */}
      <InsurancePolicyModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        editingPolicy={policy}
        onSuccess={() => {
          queryClient.invalidateQueries({
            queryKey: [`/api/compliance/insurance/${policyId}`],
          });
        }}
      />

      {/* Add Endorsement Modal */}
      <InsuranceEndorsementModal
        open={isEndorsementModalOpen}
        onOpenChange={setIsEndorsementModalOpen}
        policyId={policyId}
      />

      {/* AFG Submission Modal */}
      <AfgSubmissionModal
        open={isAfgModalOpen}
        onOpenChange={setIsAfgModalOpen}
        policyId={policyId}
      />

    </div>
  );
}

export default function InsuranceDetailPage() {
  return (
    <ComplianceLayout>
      <InsuranceDetailContent />
    </ComplianceLayout>
  );
}
